import React, { useEffect, useMemo, useRef, useState } from "react";
import { useWatch } from "react-hook-form";
import Select from "react-select";
import { getPaymentCardList } from "../../../services/MasterApiServices";

/* ==========================================================================
   Tender for the transaction terminals - the mock-up's Cash / Card / Online /
   Credit lines, quick-cash chips and balance row, shared by the sales and
   purchase screens.

   Amount mapping follows the POS screen, which already writes split bills:
     Cash -> cash   Card -> card   Online -> upi   Credit -> bank
     paid  = the four lines added up      change = each screen's own rule
   A split bill is filed under the line that carried the most, because
   `salesPaymentType` holds one value - see docs/BACKEND-COMPLETE.md (Part 5).

   Purchase screens have no split columns, so they show one "Advance paid" line
   (the `advancePaid` field) and no split toggle.
   ========================================================================== */

// value = salesPaymentType, key = the payload column the amount lands in
export const PAY_MODES = [
  { value: 0, key: "cash", field: "payCash", label: "Cash" },
  { value: 3, key: "card", field: "payCard", label: "Card" },
  { value: 2, key: "upi", field: "payOnline", label: "Online" },
  { value: 1, key: "bank", field: "payCredit", label: "Credit" },
];

// the payment types every transaction screen offers. "Online" was "UPI"; the
// stored value (2) is unchanged, so old records keep their meaning.
export const PAYMENT_TYPES = [
  { value: 0, label: "Cash" },
  { value: 1, label: "Credit" },
  { value: 2, label: "Online" },
  { value: 3, label: "Card" },
];

// Sales screens number their payment types 0 Cash, 1 Credit, 2 Online, 3 Card.
// Purchase screens use a different list (0 Cash, 1 Card, 2 Bank, 3 Credit,
// 4 Online), so the value that means "Online" is passed in.
const ONLINE_VALUE = 2;

const num = (v) => {
  if (v === null || v === undefined || v === "") return 0;
  const n = Number(v);
  return isNaN(n) ? 0 : n;
};

const readPref = (key, fallback) => {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? { ...fallback, ...JSON.parse(raw) } : fallback;
  } catch {
    return fallback;
  }
};

const writePref = (key, value) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage unavailable - the choice just will not persist */
  }
};

/**
 * @param {object} o
 * @param {string} o.screenKey           prefix for the saved split preference
 * @param {object} o.control             react-hook-form control (for useWatch)
 * @param {Function} o.setValue          react-hook-form
 * @param {number} o.netTotal            the bill total
 * @param {number} o.decimalpoints       currency decimals
 * @param {number} o.paymentType         the selected payment type value
 * @param {"sales"|"purchase"} [o.kind]  purchase screens get one line, no split
 * @param {number} [o.onlineValue]       the payment type that means "Online"
 */
export const useTender = ({
  screenKey,
  control,
  setValue,
  netTotal,
  decimalpoints = 2,
  paymentType,
  kind = "sales",
  onlineValue = ONLINE_VALUE,
}) => {
  const splitKey = `${screenKey}.tenderSplit`;
  const [split, setSplit] = useState(() =>
    kind === "sales" ? readPref(splitKey, { on: false }).on : false
  );
  const toggleSplit = () =>
    setSplit((prev) => {
      const next = !prev;
      writePref(splitKey, { on: next });
      // leaving split mode drops the per-line amounts so only `paid` counts
      if (!next) PAY_MODES.forEach((m) => setValue(m.field, 0));
      return next;
    });

  // the payment cards from the master, for an Online payment
  const [cards, setCards] = useState([]);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const response = await getPaymentCardList();
        if (!alive || !Array.isArray(response)) return;
        setCards(
          response.map((c) => ({ label: c?.cardName, value: c?.guid, card: c }))
        );
      } catch {
        /* the picker just stays empty - the sale can still be saved */
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const paidField = kind === "purchase" ? "advancePaid" : "paid";
  // useWatch rather than watch(): these screens setValue() while rendering, and
  // a render-time watch() of extra names turns that into an endless re-render
  const watched = useWatch({
    control,
    name: [...PAY_MODES.map((m) => m.field), paidField],
  });
  const lineAmounts = PAY_MODES.reduce(
    (acc, m, i) => ({ ...acc, [m.key]: num(watched?.[i]) }),
    {}
  );
  const splitTotal = PAY_MODES.reduce((s, m) => s + lineAmounts[m.key], 0);
  const paidValue = num(watched?.[PAY_MODES.length]);
  const paid = split ? splitTotal : paidValue;

  // keep `paid` in step with the lines while split is on, so the screen's own
  // change / balance figures keep working untouched
  useEffect(() => {
    if (!split) return;
    const rounded = Number(splitTotal.toFixed(decimalpoints));
    if (paidValue !== rounded) setValue(paidField, rounded);
  }, [split, splitTotal, paidValue, decimalpoints, paidField, setValue]);

  const balance = paid - num(netTotal);
  const state =
    Math.abs(balance) < 0.005 ? "ok" : balance > 0 ? "change" : "due";

  // the exact amount, then the next round notes up
  const quickCash = (() => {
    if (!(netTotal > 0)) return [];
    const out = [Number(Number(netTotal).toFixed(decimalpoints))];
    [10, 50, 100, 500].forEach((step) => {
      const v = Math.ceil(netTotal / step) * step;
      if (out.length < 4 && !out.includes(v) && v > out[0]) out.push(v);
    });
    return out;
  })();

  const onlineSelected = paymentType === onlineValue;
  const showCardPicker =
    onlineSelected || (split && lineAmounts.upi > 0) || undefined;

  return {
    kind,
    split,
    toggleSplit,
    cards,
    lineAmounts,
    paid,
    paidField,
    balance,
    state,
    quickCash,
    showCardPicker: !!showCardPicker,
    /** amounts for the payload, or null while a single payment type is used */
    amounts: split
      ? {
          ...lineAmounts,
          dominant: PAY_MODES.reduce((best, m) =>
            lineAmounts[m.key] > lineAmounts[best.key] ? m : best
          ).value,
        }
      : null,
  };
};

/**
 * Writes the tender onto a sales payload. Call it in place of the screen's
 * "whole net into one bucket" if-chain; with no split it does exactly what that
 * chain did, so nothing changes for a single payment type.
 */
export const applyTender = (
  objsales,
  { tender, paymentType, netTotal, splitPaymentType }
) => {
  const amounts = tender?.amounts;
  if (amounts) {
    objsales.cash = amounts.cash || 0;
    objsales.card = amounts.card || 0;
    objsales.upi = amounts.upi || 0;
    objsales.bank = amounts.bank || 0;
    // One value can only name one mode, so the largest line wins (as the POS
    // does). Pass splitPaymentType once the backend confirms a value that means
    // "split" - the Sales Quotation list already offers 4 = MultiPayment.
    objsales.salesPaymentType =
      splitPaymentType === undefined ? amounts.dominant : splitPaymentType;
  } else if (paymentType === 0) {
    objsales.cash = netTotal || 0;
  } else if (paymentType === 1) {
    objsales.bank = netTotal || 0;
  } else if (paymentType === 2) {
    objsales.upi = netTotal || 0;
  } else if (paymentType === 3) {
    objsales.card = netTotal || 0;
  }
  return objsales;
};

/** The rail's Tender card. */
export const TermTender = ({
  tender,
  register,
  setValue,
  control,
  fmt,
  decimalpoints = 2,
  paymentLabel,
  error,
  menuPortalTarget,
  selectStyles,
  Controller,
}) => {
  const single = !tender.split;
  const purchase = tender.kind === "purchase";
  const balanceText =
    tender.state === "ok"
      ? purchase
        ? "Settled in full"
        : "Settled in full"
      : tender.state === "change"
      ? purchase
        ? "Advance over the bill"
        : "Change to return"
      : purchase
      ? "Still to pay"
      : "Still to collect";

  return (
    <div className="si-card">
      <div className="si-card-h">
        <span>Tender</span>
        <span className="r">
          {purchase ? (
            <span className="si-kbd">F4</span>
          ) : (
            <button
              type="button"
              className="si-mini"
              aria-pressed={tender.split}
              onClick={tender.toggleSplit}
              title="Split this bill across payment modes"
            >
              Split
            </button>
          )}
        </span>
      </div>
      <div className="si-tender">
        {single ? (
          <div className={`si-tline${tender.paid > 0 ? " si-tline--on" : ""}`}>
            <span className="tl">
              {purchase ? "Advance" : paymentLabel || "Paid"}
            </span>
            <input
              type="text"
              inputMode="decimal"
              aria-label={purchase ? "Advance paid" : "Amount paid"}
              data-term-field="tender"
              onFocus={(e) => e.target.select()}
              {...register(tender.paidField)}
              defaultValue={0}
            />
          </div>
        ) : (
          <>
            <div
              className={`si-tline${
                tender.lineAmounts.cash > 0 ? " si-tline--on" : ""
              }`}
            >
              <span className="tl">Cash</span>
              <input
                type="text"
                inputMode="decimal"
                aria-label="Cash"
                data-term-field="tender"
                onFocus={(e) => e.target.select()}
                {...register("payCash")}
              />
            </div>
            <div className="si-tsplit">
              <div
                className={`si-tline${
                  tender.lineAmounts.card > 0 ? " si-tline--on" : ""
                }`}
              >
                <span className="tl">Card</span>
                <input
                  type="text"
                  inputMode="decimal"
                  aria-label="Card"
                  onFocus={(e) => e.target.select()}
                  {...register("payCard")}
                />
              </div>
              <div
                className={`si-tline${
                  tender.lineAmounts.upi > 0 ? " si-tline--on" : ""
                }`}
              >
                <span className="tl">Online</span>
                <input
                  type="text"
                  inputMode="decimal"
                  aria-label="Online"
                  onFocus={(e) => e.target.select()}
                  {...register("payOnline")}
                />
              </div>
            </div>
            <div
              className={`si-tline${
                tender.lineAmounts.bank > 0 ? " si-tline--on" : ""
              }`}
            >
              <span className="tl">Credit</span>
              <input
                type="text"
                inputMode="decimal"
                aria-label="Credit on account"
                onFocus={(e) => e.target.select()}
                {...register("payCredit")}
              />
            </div>
          </>
        )}

        {/* which card machine / scheme took an online payment */}
        {tender.showCardPicker && (
          <div className="si-cardsel">
            <span className="tl">Card</span>
            <Controller
              control={control}
              name="paymentCard"
              render={({ field: { onChange, value } }) => (
                <Select
                  classNamePrefix="react-select"
                  className="si-sel"
                  options={tender.cards}
                  placeholder={
                    tender.cards.length ? "Select card" : "No cards in master"
                  }
                  isClearable
                  value={tender.cards.find((c) => c.value === value) ?? null}
                  onChange={(opt) => onChange(opt ? opt.value : null)}
                  menuPortalTarget={menuPortalTarget}
                  menuPosition="fixed"
                  styles={selectStyles}
                />
              )}
            />
          </div>
        )}

        {error && <div className="si-tr-err">{error}</div>}

        {!purchase && tender.quickCash.length > 0 && (
          <div className="si-quick">
            {tender.quickCash.map((v, i) => (
              <button
                key={v}
                type="button"
                onClick={() =>
                  setValue(
                    tender.split ? "payCash" : tender.paidField,
                    v.toFixed(decimalpoints)
                  )
                }
              >
                {i === 0 ? "Exact " : ""}
                {v.toFixed(i === 0 ? decimalpoints : 0)}
              </button>
            ))}
          </div>
        )}

        <div className={`si-balrow si-balrow--${tender.state}`}>
          <span>{balanceText}</span>
          <span className="v">{fmt(Math.abs(tender.balance))}</span>
        </div>
      </div>
    </div>
  );
};

/** Round off with the mock-up's Auto chip: on, it rounds the bill to a whole unit. */
export const TermRoundOff = ({
  screenKey,
  register,
  setValue,
  control,
  netTotal,
  decimalpoints = 2,
  error,
  rules,
}) => {
  // register() has to get the SAME rules object on every render: a fresh one
  // re-registers the field, and on these screens (they setValue while
  // rendering) that becomes an endless re-render. This proxy keeps one identity
  // while still calling the caller's current validate, so its message and the
  // totals it closes over stay current.
  const rulesRef = useRef(rules);
  rulesRef.current = rules;
  const stableRules = useMemo(
    () => ({
      validate: (value) =>
        rulesRef.current?.validate ? rulesRef.current.validate(value) : true,
    }),
    []
  );

  const key = `${screenKey}.roundAuto`;
  const [auto, setAuto] = useState(() => readPref(key, { on: false }).on);
  const toggleAuto = () =>
    setAuto((prev) => {
      const next = !prev;
      writePref(key, { on: next });
      if (!next) setValue("round", 0);
      return next;
    });

  // netTotal already has the current round-off taken off, so add it back before
  // working out what the rounding should be
  const rounded = num(useWatch({ control, name: "round" }));
  const beforeRound = num(netTotal) + rounded;
  const target = Number((beforeRound - Math.round(beforeRound)).toFixed(decimalpoints));
  useEffect(() => {
    if (!auto) return;
    if (Math.abs(target - rounded) > 0.0049) setValue("round", target);
  }, [auto, target, rounded, setValue]);

  return (
    <>
      <div className="si-tr si-tr--edit">
        <span className="l">Round off</span>
        <button
          type="button"
          className="si-mini"
          aria-pressed={auto}
          onClick={toggleAuto}
          title="Round the bill to a whole unit"
        >
          {auto ? "Auto" : "Off"}
        </button>
        <span className="v">
          <input
            type="number"
            step="any"
            min={0}
            inputMode="decimal"
            readOnly={auto}
            defaultValue={0}
            {...register("round", stableRules)}
          />
        </span>
      </div>
      {error && <div className="si-tr-err">{error}</div>}
    </>
  );
};
